"use strict";

// js gallery
class ZoomAction extends HTMLElement {
  constructor() {
    super();
    this.type = this.dataset.type || "no_zoom";
    this.zoomOption = this.dataset.zoomOption || "external";
    this.modal = null;
    this.mainSwiper = null;
    this.thumbSwiper = null;
    this.drift = null;
    this.lastTrigger = null;
    this.lightboxItems = [];
    this.lightboxOpenToken = 0;
    this.lightboxRenderFrame = null;
    this.lightboxPreloadRadius = 2;
    this.isInitialized = false;
    this.lightboxBound = false;
    this.boundResponsive = this.responsive.bind(this);
    this.boundLightboxTrigger = this.handleLightboxTrigger.bind(this);
    this.boundModalClick = this.handleModalClick.bind(this);
    this.boundModalPointerdown = this.handleModalPointerdown.bind(this);
    this.boundDocumentKeydown = this.handleDocumentKeydown.bind(this);
    this.boundFullscreenChange = this.handleFullscreenChange.bind(this);
  }

  connectedCallback() {
    if (this.isInitialized) return;
    this.isInitialized = true;
    queueMicrotask(() => {
      if (!this.isConnected) return;
      this.init();
    });
  }

  init() {
    if (this.type === "no_zoom") return;
    this.initLightBox();
    window.addEventListener("resize", this.boundResponsive);
    if (document.readyState === "complete") {
      this.boundResponsive();
      return;
    }
    window.addEventListener("load", this.boundResponsive, { once: true });
  }

  initLightBox() {
    if (this.lightboxBound) return;
    this.lightboxBound = true;
    this.addEventListener("click", this.boundLightboxTrigger);
  }

  removeLightBoxBinding() {
    if (!this.lightboxBound) return;
    this.removeEventListener("click", this.boundLightboxTrigger);
    this.lightboxBound = false;
  }

  getLightboxItems() {
    return Array.from(this.querySelectorAll("a.media-gallery__image")).filter(
      (item) =>
        !item.closest(".swiper-slide")?.classList.contains(
          "swiper-slide-duplicate"
        )
    );
  }

  resolveLightboxIndex(target, galleryItems) {
    const targetIndex = galleryItems.indexOf(target);

    if (targetIndex >= 0) {
      return targetIndex;
    }

    const mediaId = target?.getAttribute("data-media-id");

    if (mediaId) {
      const mediaIdIndex = galleryItems.findIndex(
        (item) => item.getAttribute("data-media-id") === mediaId
      );

      if (mediaIdIndex >= 0) {
        return mediaIdIndex;
      }
    }

    const position = Number.parseInt(target?.getAttribute("data-position"), 10);

    if (Number.isFinite(position) && position > 0) {
      const positionIndex = galleryItems.findIndex(
        (item) =>
          Number.parseInt(item.getAttribute("data-position"), 10) === position
      );

      if (positionIndex >= 0) {
        return positionIndex;
      }

      return Math.min(position - 1, galleryItems.length - 1);
    }

    return 0;
  }

  getItemImageSrc(item) {
    const rawSrc =
      item.getAttribute("href") ||
      item.querySelector("img")?.currentSrc ||
      item.querySelector("img")?.src ||
      "";

    if (!rawSrc) return "";

    return new URL(rawSrc, window.location.href).href;
  }

  getItemThumbnailSrc(item) {
    const rawSrc =
      item.getAttribute("data-thumbnail") ||
      item.querySelector("img")?.currentSrc ||
      item.querySelector("img")?.src ||
      item.getAttribute("href") ||
      "";

    if (!rawSrc) return "";

    return new URL(rawSrc, window.location.href).href;
  }

  getItemImageAlt(item) {
    return (
      item.getAttribute("data-alt") ||
      item.querySelector("img")?.getAttribute("alt") ||
      ""
    );
  }

  getLightboxItemData(item) {
    const imageSrc = this.getItemImageSrc(item);

    if (!imageSrc) return null;

    const imageWidth = Number.parseInt(
      item.getAttribute("data-pswp-width"),
      10
    );
    const imageHeight = Number.parseInt(
      item.getAttribute("data-pswp-height"),
      10
    );

    return {
      imageSrc,
      thumbnailSrc: this.getItemThumbnailSrc(item) || imageSrc,
      imageAlt: this.getItemImageAlt(item),
      imageWidth,
      imageHeight,
      watermarkHtml:
        item.querySelector(".product-image-watermarks")?.outerHTML ||
        item.querySelector(".product-image-watermark")?.outerHTML ||
        "",
    };
  }

  createLightboxModal() {
    if (this.modal) return this.modal;

    const modal = document.createElement("div");

    modal.className = "product-lightbox";
    modal.setAttribute("aria-hidden", "true");
    modal.innerHTML = `
      <div class="product-lightbox__surface" role="dialog" aria-modal="true" aria-label="Product gallery">
        <div class="product-lightbox__toolbar">
          <div class="product-lightbox__counter" aria-live="polite"></div>
          <div class="product-lightbox__toolbar-actions">
            <button class="product-lightbox__tool product-lightbox__tool--zoom" type="button" data-lightbox-action="zoom" aria-label="Zoom in" title="Zoom in" aria-pressed="false">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <circle cx="10.5" cy="10.5" r="5.5" />
                <path d="M10.5 8v5M8 10.5h5M15.5 15.5 20 20" />
              </svg>
            </button>
            <button class="product-lightbox__tool product-lightbox__tool--fullscreen" type="button" data-lightbox-action="fullscreen" aria-label="Enter fullscreen" title="Enter fullscreen" aria-pressed="false">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M8 4H4v4M16 4h4v4M20 16v4h-4M4 16v4h4" />
              </svg>
            </button>
          </div>
          <button class="product-lightbox__close" type="button" data-lightbox-close aria-label="Close product gallery">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M6 6 18 18M18 6 6 18" />
            </svg>
          </button>
        </div>
        <div class="product-lightbox__main swiper">
          <div class="swiper-wrapper"></div>
          <button class="product-lightbox__arrow product-lightbox__arrow--prev" type="button" aria-label="Previous image">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="m14.5 18-6-6 6-6" />
            </svg>
          </button>
          <button class="product-lightbox__arrow product-lightbox__arrow--next" type="button" aria-label="Next image">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="m9.5 6 6 6-6 6" />
            </svg>
          </button>
        </div>
        <div class="product-lightbox__loader" aria-hidden="true"></div>
        <div class="product-lightbox__thumbs-shell">
          <div class="product-lightbox__thumbs swiper">
            <div class="swiper-wrapper"></div>
          </div>
        </div>
      </div>
    `;

    modal.addEventListener("click", this.boundModalClick);
    modal.addEventListener("pointerdown", this.boundModalPointerdown, true);
    document.body.appendChild(modal);
    this.modal = modal;
    return modal;
  }

  renderLightboxSlides(galleryItems, activePosition = 0) {
    const modal = this.createLightboxModal();
    const mainWrapper = modal.querySelector(
      ".product-lightbox__main .swiper-wrapper"
    );
    const thumbsWrapper = modal.querySelector(
      ".product-lightbox__thumbs .swiper-wrapper"
    );

    if (!mainWrapper || !thumbsWrapper) return 0;

    const lightboxItems = galleryItems
      .map((item) => this.getLightboxItemData(item))
      .filter(Boolean);
    const totalItems = lightboxItems.length;
    const safeActivePosition = Math.max(
      0,
      Math.min(activePosition, totalItems - 1)
    );
    const mainFragment = document.createDocumentFragment();
    const thumbsFragment = document.createDocumentFragment();

    this.lightboxItems = lightboxItems;
    mainWrapper.textContent = "";
    thumbsWrapper.textContent = "";

    lightboxItems.forEach((item, index) => {
      const mainSlide = document.createElement("div");
      const zoomContainer = document.createElement("div");
      const mediaFrame = document.createElement("div");
      const mainImage = document.createElement("img");

      mainSlide.className = "swiper-slide product-lightbox__slide";
      mainSlide.dataset.lightboxIndex = String(index);
      zoomContainer.className = "swiper-zoom-container product-lightbox__zoom";
      mediaFrame.className = "swiper-zoom-target product-lightbox__image-frame";
      mainImage.className = "product-lightbox__image";
      mainImage.dataset.src = item.imageSrc;
      mainImage.alt = item.imageAlt;
      mainImage.loading = index === safeActivePosition ? "eager" : "lazy";
      mainImage.decoding = "async";
      mainImage.draggable = false;

      if (index === safeActivePosition) {
        mainImage.fetchPriority = "high";
      }

      if (Number.isFinite(item.imageWidth) && item.imageWidth > 0) {
        mainImage.width = item.imageWidth;
      }

      if (Number.isFinite(item.imageHeight) && item.imageHeight > 0) {
        mainImage.height = item.imageHeight;
      }

      if (
        Number.isFinite(item.imageWidth) &&
        item.imageWidth > 0 &&
        Number.isFinite(item.imageHeight) &&
        item.imageHeight > 0
      ) {
        mediaFrame.style.setProperty(
          "--product-lightbox-image-ratio",
          item.imageWidth / item.imageHeight
        );
      }

      mediaFrame.appendChild(mainImage);
      if (item.watermarkHtml) {
        const template = document.createElement("template");
        template.innerHTML = item.watermarkHtml.trim();
        const watermark = template.content.firstElementChild;

        if (watermark) {
          watermark.classList.add("product-image-watermarks--lightbox");
          mediaFrame.appendChild(watermark);
        }
      }
      zoomContainer.appendChild(mediaFrame);
      mainSlide.appendChild(zoomContainer);
      mainFragment.appendChild(mainSlide);

      const thumbSlide = document.createElement("div");
      const thumbImage = document.createElement("img");

      thumbSlide.className = "swiper-slide product-lightbox__thumb-slide";
      thumbSlide.dataset.lightboxIndex = String(index);
      thumbImage.src = item.thumbnailSrc;
      thumbImage.alt = item.imageAlt;
      thumbImage.loading = "lazy";
      thumbImage.decoding = "async";
      thumbImage.draggable = false;
      thumbSlide.appendChild(thumbImage);
      thumbsFragment.appendChild(thumbSlide);
    });

    mainWrapper.appendChild(mainFragment);
    thumbsWrapper.appendChild(thumbsFragment);
    this.loadLightboxImageRange(safeActivePosition);

    const slideCount = lightboxItems.length;
    const hasMultiple = slideCount > 1;
    const thumbsShell = modal.querySelector(".product-lightbox__thumbs-shell");

    modal.classList.toggle("product-lightbox--single", !hasMultiple);
    if (thumbsShell) {
      thumbsShell.hidden = !hasMultiple;
    }

    modal.querySelectorAll(".product-lightbox__arrow").forEach((arrow) => {
      arrow.hidden = !hasMultiple;
    });

    return slideCount;
  }

  loadLightboxSlideImage(index, priority = false) {
    const image = this.modal?.querySelector(
      `.product-lightbox__slide[data-lightbox-index="${index}"] .product-lightbox__image`
    );

    if (!image || image.dataset.loaded === "true") return;

    const src = image.dataset.src;

    if (!src) return;

    image.loading = priority ? "eager" : "lazy";
    image.fetchPriority = priority ? "high" : "low";
    image.src = src;
    image.dataset.loaded = "true";
  }

  loadLightboxImageRange(index) {
    if (!this.lightboxItems.length) return;

    const activeIndex = Math.max(
      0,
      Math.min(index, this.lightboxItems.length - 1)
    );

    for (
      let slideIndex = activeIndex - this.lightboxPreloadRadius;
      slideIndex <= activeIndex + this.lightboxPreloadRadius;
      slideIndex += 1
    ) {
      this.loadLightboxSlideImage(slideIndex, slideIndex === activeIndex);
    }
  }

  cancelLightboxRenderFrame() {
    if (!this.lightboxRenderFrame) return;

    cancelAnimationFrame(this.lightboxRenderFrame);
    this.lightboxRenderFrame = null;
  }

  isCurrentLightboxOpen(openToken) {
    return Boolean(
      this.lightboxOpenToken === openToken &&
        this.modal?.classList.contains("is-active")
    );
  }

  setLightboxLoading(isLoading) {
    this.modal?.classList.toggle("is-loading", isLoading);
  }

  getFullscreenTarget() {
    return this.modal?.querySelector(".product-lightbox__surface") || null;
  }

  isFullscreenSupported() {
    return Boolean(
      document.fullscreenEnabled || document.webkitFullscreenEnabled
    );
  }

  getFullscreenElement() {
    return document.fullscreenElement || document.webkitFullscreenElement || null;
  }

  isLightboxFullscreen() {
    const fullscreenElement = this.getFullscreenElement();
    const fullscreenTarget = this.getFullscreenTarget();

    return Boolean(
      fullscreenElement &&
        fullscreenTarget &&
        (fullscreenElement === fullscreenTarget ||
          fullscreenTarget.contains(fullscreenElement))
    );
  }

  requestFullscreen(target) {
    if (!target) return;

    try {
      const fullscreenPromise =
        target.requestFullscreen?.() || target.webkitRequestFullscreen?.();

      fullscreenPromise?.catch?.((error) => {
        console.error(error);
      });
    } catch (error) {
      console.error(error);
    }
  }

  exitFullscreen() {
    try {
      const fullscreenPromise =
        document.exitFullscreen?.() || document.webkitExitFullscreen?.();

      fullscreenPromise?.catch?.((error) => {
        console.error(error);
      });
    } catch (error) {
      console.error(error);
    }
  }

  updateFullscreenButtonState() {
    const fullscreenButton = this.modal?.querySelector(
      '[data-lightbox-action="fullscreen"]'
    );

    if (!fullscreenButton) return;

    const isSupported = this.isFullscreenSupported();
    const isActive = isSupported && this.isLightboxFullscreen();

    fullscreenButton.hidden = !isSupported;
    fullscreenButton.classList.toggle("is-active", isActive);
    fullscreenButton.setAttribute("aria-pressed", isActive ? "true" : "false");
    fullscreenButton.setAttribute(
      "aria-label",
      isActive ? "Exit fullscreen" : "Enter fullscreen"
    );
    fullscreenButton.setAttribute(
      "title",
      isActive ? "Exit fullscreen" : "Enter fullscreen"
    );
  }

  updateZoomButtonState() {
    const zoomButton = this.modal?.querySelector('[data-lightbox-action="zoom"]');

    if (!zoomButton) return;

    const currentScale = Number(this.mainSwiper?.zoom?.scale) || 1;
    const isZoomed = currentScale > 1.01;
    const hasZoom = Boolean(this.mainSwiper?.zoom);

    zoomButton.hidden = !hasZoom;
    zoomButton.classList.toggle("is-active", isZoomed);
    zoomButton.setAttribute("aria-pressed", isZoomed ? "true" : "false");
    zoomButton.setAttribute("aria-label", isZoomed ? "Reset zoom" : "Zoom in");
    zoomButton.setAttribute("title", isZoomed ? "Reset zoom" : "Zoom in");
    this.modal?.classList.toggle("is-zoomed", isZoomed);
  }

  handleFullscreenChange() {
    this.updateFullscreenButtonState();
  }

  toggleFullscreen() {
    if (!this.isFullscreenSupported()) return;

    if (this.isLightboxFullscreen()) {
      this.exitFullscreen();
      return;
    }

    this.requestFullscreen(this.getFullscreenTarget());
  }

  resetZoom() {
    if (!this.mainSwiper?.zoom) return;

    if ((Number(this.mainSwiper.zoom.scale) || 1) <= 1.01) {
      this.updateZoomButtonState();
      return;
    }

    this.mainSwiper.zoom.out();
    this.updateZoomButtonState();
  }

  toggleZoom() {
    if (!this.mainSwiper?.zoom) return;

    const currentScale = Number(this.mainSwiper.zoom.scale) || 1;

    if (currentScale > 1.01) {
      this.mainSwiper.zoom.out();
    } else {
      this.mainSwiper.zoom.in(2);
    }

    this.updateZoomButtonState();
  }

  updateLightboxCounter(totalItems, fallbackIndex = 0) {
    if (!this.modal) return;

    const counter = this.modal.querySelector(".product-lightbox__counter");

    if (!counter) return;

    if (totalItems <= 0) {
      counter.textContent = "";
      return;
    }

    const swiperIndex =
      this.mainSwiper?.realIndex ?? this.mainSwiper?.activeIndex;
    const activeIndex = Math.max(
      Number.isFinite(swiperIndex) ? swiperIndex : fallbackIndex,
      0
    );

    counter.textContent = `${Math.min(activeIndex + 1, totalItems)} / ${totalItems}`;
  }

  syncLightboxThumbPosition() {
    if (!this.thumbSwiper || this.thumbSwiper.destroyed || !this.mainSwiper) {
      return;
    }

    const activeIndex = Math.max(
      this.mainSwiper.realIndex ?? this.mainSwiper.activeIndex ?? 0,
      0
    );

    this.thumbSwiper.slideTo(Math.max(activeIndex - 1, 0), 300);
  }

  destroyLightboxSwipers() {
    if (this.mainSwiper && !this.mainSwiper.destroyed) {
      this.mainSwiper.destroy(true, true);
    }
    if (this.thumbSwiper && !this.thumbSwiper.destroyed) {
      this.thumbSwiper.destroy(true, true);
    }

    this.mainSwiper = null;
    this.thumbSwiper = null;
  }

  initLightboxSwipers(position, totalItems) {
    if (!this.modal || typeof Swiper === "undefined") return;

    const thumbsElement = this.modal.querySelector(".product-lightbox__thumbs");
    const mainElement = this.modal.querySelector(".product-lightbox__main");

    this.destroyLightboxSwipers();

    if (totalItems > 1 && thumbsElement) {
      this.thumbSwiper = new Swiper(thumbsElement, {
        slidesPerView: "auto",
        centeredSlides: false,
        spaceBetween: 8,
        speed: 300,
        freeMode: true,
        watchSlidesProgress: true,
        watchOverflow: false,
        slideToClickedSlide: true,
        breakpoints: {
          768: {
            spaceBetween: 12,
          },
        },
      });
    }

    if (!mainElement) return;

    this.mainSwiper = new Swiper(mainElement, {
      initialSlide: Math.max(0, Math.min(position, totalItems - 1)),
      slidesPerView: 1,
      centeredSlides: false,
      loop: false,
      slidesOffsetBefore: 0,
      slidesOffsetAfter: 0,
      speed: 350,
      spaceBetween: 0,
      zoom: {
        enabled: true,
        maxRatio: 3,
        minRatio: 1,
        toggle: false,
      },
      watchOverflow: false,
      navigation:
        totalItems > 1
          ? {
              nextEl: this.modal.querySelector(
                ".product-lightbox__arrow--next"
              ),
              prevEl: this.modal.querySelector(
                ".product-lightbox__arrow--prev"
              ),
            }
          : undefined,
      thumbs: this.thumbSwiper ? { swiper: this.thumbSwiper } : undefined,
      on: {
        init: () => {
          this.updateLightboxCounter(totalItems);
          this.syncLightboxThumbPosition();
          this.updateZoomButtonState();
          this.updateFullscreenButtonState();
        },
        slideChange: () => {
          const activeIndex = Math.max(
            this.mainSwiper?.realIndex ?? this.mainSwiper?.activeIndex ?? 0,
            0
          );

          this.loadLightboxImageRange(activeIndex);
          this.updateLightboxCounter(totalItems);
          this.syncLightboxThumbPosition();
          this.updateZoomButtonState();
        },
        zoomChange: () => {
          this.updateZoomButtonState();
        },
      },
    });
  }

  openLightbox(position, galleryItems, trigger) {
    const totalItems = galleryItems.length;

    if (totalItems <= 0) return;

    const safePosition = Math.max(0, Math.min(position, totalItems - 1));
    const openToken = this.lightboxOpenToken + 1;

    this.lightboxOpenToken = openToken;
    this.lastTrigger = trigger || null;
    this.createLightboxModal();
    this.destroyLightboxSwipers();
    this.cancelLightboxRenderFrame();
    this.modal.classList.add("is-active");
    this.modal.setAttribute("aria-hidden", "false");
    this.setLightboxLoading(true);
    this.updateZoomButtonState();
    document.documentElement.classList.add("overflow-hidden");
    document.addEventListener("keydown", this.boundDocumentKeydown);
    document.addEventListener("fullscreenchange", this.boundFullscreenChange);
    document.addEventListener(
      "webkitfullscreenchange",
      this.boundFullscreenChange
    );
    this.updateFullscreenButtonState();
    this.updateLightboxCounter(totalItems, safePosition);

    const initializeLightbox = () => {
      this.lightboxRenderFrame = null;

      if (!this.isCurrentLightboxOpen(openToken)) return;

      if (typeof Swiper === "undefined") {
        this.lightboxRenderFrame = requestAnimationFrame(initializeLightbox);
        return;
      }

      const renderedItems = this.renderLightboxSlides(
        galleryItems,
        safePosition
      );

      if (renderedItems <= 0 || !this.isCurrentLightboxOpen(openToken)) return;

      const renderedPosition = Math.max(
        0,
        Math.min(safePosition, renderedItems - 1)
      );

      this.initLightboxSwipers(renderedPosition, renderedItems);

      requestAnimationFrame(() => {
        if (!this.isCurrentLightboxOpen(openToken)) return;

        this.mainSwiper?.update();
        this.mainSwiper?.slideTo(renderedPosition, 0, false);
        this.thumbSwiper?.update();
        this.syncLightboxThumbPosition();
        this.updateZoomButtonState();
        this.setLightboxLoading(false);
        this.modal
          ?.querySelector(".product-lightbox__close")
          ?.focus({ preventScroll: true });
      });
    };

    this.lightboxRenderFrame = requestAnimationFrame(initializeLightbox);
  }

  closeLightbox({ restoreFocus = true } = {}) {
    if (!this.modal?.classList.contains("is-active")) return;

    this.lightboxOpenToken += 1;
    this.cancelLightboxRenderFrame();
    this.resetZoom();
    if (this.isLightboxFullscreen()) {
      this.exitFullscreen();
    }

    this.modal.classList.remove("is-active");
    this.setLightboxLoading(false);
    this.modal.setAttribute("aria-hidden", "true");
    document.documentElement.classList.remove("overflow-hidden");
    document.removeEventListener("keydown", this.boundDocumentKeydown);
    document.removeEventListener(
      "fullscreenchange",
      this.boundFullscreenChange
    );
    document.removeEventListener(
      "webkitfullscreenchange",
      this.boundFullscreenChange
    );
    this.destroyLightboxSwipers();

    if (restoreFocus && this.lastTrigger?.isConnected) {
      this.lastTrigger.focus({ preventScroll: true });
    }
  }

  destroyLightboxModal() {
    this.closeLightbox({ restoreFocus: false });

    if (!this.modal) return;

    this.modal.removeEventListener("click", this.boundModalClick);
    this.modal.removeEventListener(
      "pointerdown",
      this.boundModalPointerdown,
      true
    );
    this.modal.remove();
    this.modal = null;
  }

  handleModalPointerdown(event) {
    const thumbSlide = event.target.closest(".product-lightbox__thumb-slide");

    if (!thumbSlide || !this.modal?.contains(thumbSlide)) return;

    const thumbIndex = Number.parseInt(thumbSlide.dataset.lightboxIndex, 10);

    if (Number.isFinite(thumbIndex)) {
      this.loadLightboxImageRange(thumbIndex);
    }
  }

  handleModalClick(event) {
    if (event.target === this.modal) {
      this.closeLightbox();
      return;
    }

    const lightboxImage = event.target.closest(".product-lightbox__image");

    if (lightboxImage && this.modal?.contains(lightboxImage)) {
      if (this.mainSwiper?.allowClick === false) return;

      event.preventDefault();
      this.toggleZoom();
      return;
    }

    const thumbSlide = event.target.closest(".product-lightbox__thumb-slide");

    if (thumbSlide && this.modal?.contains(thumbSlide)) {
      const thumbIndex = Number.parseInt(thumbSlide.dataset.lightboxIndex, 10);

      if (Number.isFinite(thumbIndex)) {
        this.loadLightboxImageRange(thumbIndex);
      }
    }

    const actionButton = event.target.closest("[data-lightbox-action]");

    if (actionButton) {
      event.preventDefault();

      if (actionButton.dataset.lightboxAction === "zoom") {
        this.toggleZoom();
        return;
      }

      if (actionButton.dataset.lightboxAction === "fullscreen") {
        this.toggleFullscreen();
      }
      return;
    }

    const closeButton = event.target.closest("[data-lightbox-close]");

    if (!closeButton) return;

    event.preventDefault();
    this.closeLightbox();
  }

  handleDocumentKeydown(event) {
    if (!this.modal?.classList.contains("is-active")) return;

    if (event.key === "Escape") {
      event.preventDefault();
      this.closeLightbox();
      return;
    }

    if (event.key === "ArrowLeft") {
      event.preventDefault();
      this.mainSwiper?.slidePrev();
      return;
    }

    if (event.key === "ArrowRight") {
      event.preventDefault();
      this.mainSwiper?.slideNext();
      return;
    }

    if (event.key === "f" || event.key === "F") {
      event.preventDefault();
      this.toggleFullscreen();
      return;
    }

    if (event.key === "+" || event.key === "=") {
      event.preventDefault();
      this.toggleZoom();
      return;
    }

    if (event.key === "-" || event.key === "_") {
      event.preventDefault();
      this.resetZoom();
      return;
    }

    if (event.key === "0") {
      event.preventDefault();
      this.resetZoom();
    }
  }

  handleLightboxTrigger(event) {
    const target = event.target.closest("a.media-gallery__image");

    if (!target || !this.contains(target)) {
      return;
    }

    event.preventDefault();

    const galleryItems = this.getLightboxItems();

    if (galleryItems.length === 0) return;

    const position = this.resolveLightboxIndex(target, galleryItems);

    this.openLightbox(position, galleryItems, target);
  }

  initDrift() {
    if (this.drift !== null || typeof Drift === "undefined") return;

    const allTriggers = this.querySelectorAll(".drift-trigger");
    const paneContainer = this
      .closest(".sec__featured-product")
      ?.querySelector(".zoom-external-area");

    if (allTriggers.length === 0) return;

    allTriggers.forEach((trigger) => {
      const inlineContainer = trigger.closest("[data-pane-container]");
      const zoomOption = this.zoomOption;

      if (zoomOption === "inner-2") {
        this.drift = new Drift(trigger, {
          inlinePane: zoomOption === "inner-2",
          zoomFactor: 3,
          containInline: paneContainer,
          paneContainer: inlineContainer,
          hoverBoundingBox: zoomOption === "external",
          onShow: function () {
            inlineContainer?.classList.add("relative");
          },
          onHide: function () {
            inlineContainer?.classList.remove("relative");
          },
        });
        return;
      }

      this.drift = new Drift(trigger, {
        inlinePane: zoomOption,
        zoomFactor: 5,
        containInline: paneContainer,
        paneContainer:
          zoomOption === "external" ? paneContainer : inlineContainer,
        hoverBoundingBox: zoomOption === "external",
        onShow: function () {
          inlineContainer?.classList.add("relative");
        },
        onHide: function () {
          inlineContainer?.classList.remove("relative");
        },
      });
    });
  }

  responsive() {
    if (window.innerWidth >= 768) {
      if (this.type === "open_lightbox") {
        this.initLightBox();
      } else {
        this.closeLightbox({ restoreFocus: false });
        this.removeLightBoxBinding();
        if (this.drift === null) {
          this.initDrift();
        }
      }
    } else {
      if (this.type !== "open_lightbox" && this.drift !== null) {
        this.drift.destroy();
        this.drift = null;
      }
      this.initLightBox();
    }

    this.updateSwiper();
    this.mainSwiper?.update();
    this.thumbSwiper?.update();
  }

  updateSwiper() {
    if (typeof Swiper === "undefined") return;

    const thumbnailSwiper = this.querySelector(".thumbnail-slide")?.swiper;

    if (thumbnailSwiper && !thumbnailSwiper.destroyed) {
      thumbnailSwiper.update();
    }
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.boundResponsive);
    window.removeEventListener("load", this.boundResponsive);
    document.removeEventListener("keydown", this.boundDocumentKeydown);
    document.removeEventListener("fullscreenchange", this.boundFullscreenChange);
    document.removeEventListener(
      "webkitfullscreenchange",
      this.boundFullscreenChange
    );

    this.removeLightBoxBinding();
    this.destroyLightboxModal();

    if (this.drift !== null) {
      this.drift.destroy();
      this.drift = null;
    }
  }
}

customElements.define("zoom-action", ZoomAction);
